/**
 * Product Variant Card
 *
 * Card type for an optional product that has variations. Shows the product header
 * (image, title, info, "from" price) with the variant mini-cards always inline
 * beneath. The parent product is never added on its own — the user must pick a
 * variant, whose product ID is what enters the configuration.
 *
 * @package
 */

import PropTypes from 'prop-types';
import { __, sprintf } from '@wordpress/i18n';
import { decodeHtmlEntities } from '../helpers/decodeHtmlEntities';
import { getImageAltText } from '../helpers/getImageAltText';
import { getPriceFromProduct, formatPrice } from '../helpers/priceHelpers';
import VariantSelector from './VariantSelector';
import VariantDropdown from './VariantDropdown';
import QuantitySelector from './QuantitySelector';

// Quantity is stored against the chosen variant's ID, but the opt-in flag is read from
// the parent, so QuickCommerce only needs it set once per product.
//
// Mini-cards are used only for a small, image-complete set of variants; otherwise
// a dropdown keeps the card compact and scannable.
const MAX_VARIANT_CARDS = 6;

const ProductVariantCard = ( {
	productData,
	variants,
	activeVariantId,
	onSelectVariant,
	onInfoClick,
	vatMode,
	isDisabled,
	allowQuantity,
	quantity,
	onQuantityChange,
} ) => {
	const title = decodeHtmlEntities( productData.title?.rendered ) || __( 'Product', 'at-vehicle-configurator' );
	const fromPrice = getPriceFromProduct( productData, vatMode );
	const isSelected = activeVariantId !== null && activeVariantId !== undefined;
	const chooseLabel = __( 'Choose a variation', 'at-vehicle-configurator' );
	const useDropdown = variants.length > MAX_VARIANT_CARDS || ! variants.every( v => v.image );

	return (
		<div
			className={ `configurator-product-variant-card ${ isSelected ? 'selected' : '' } ${ isDisabled ? 'disabled' : '' }` }
		>
			<div className="configurator-product-variant-card-header">
				{ productData.featured_media_url && (
					<div className="configurator-product-variant-card-image">
						<img
							src={ productData.featured_media_url }
							alt={ getImageAltText( productData, 'Product' ) }
							loading="lazy"
							decoding="async"
						/>
					</div>
				) }
				<div className="configurator-product-variant-card-info">
					<h4>{ title }</h4>
					{ fromPrice > 0 && (
						<div className="configurator-product-price">
							{ sprintf(
								/* translators: %s: lowest variant price */
								__( 'From %s', 'at-vehicle-configurator' ),
								formatPrice( fromPrice )
							) }
						</div>
					) }
				</div>
				<button
					type="button"
					className="configurator-product-card-info-icon"
					onClick={ onInfoClick }
					aria-label={ sprintf(
						/* translators: %s: product name */
						__( 'View information about %s', 'at-vehicle-configurator' ),
						title
					) }
				>
					<span className="screen-reader-text">{ __( 'Information', 'at-vehicle-configurator' ) }</span>
					i
				</button>
			</div>
			{ useDropdown ? (
				<VariantDropdown
					variants={ variants }
					selectedVariant={ activeVariantId }
					onSelect={ onSelectVariant }
					vatMode={ vatMode }
					label={ chooseLabel }
					disabled={ isDisabled }
				/>
			) : (
				<VariantSelector
					variants={ variants }
					selectedVariant={ activeVariantId }
					onSelect={ onSelectVariant }
					vatMode={ vatMode }
					heading={ chooseLabel }
					disabled={ isDisabled }
				/>
			) }
			{ allowQuantity && isSelected && (
				<QuantitySelector
					quantity={ quantity }
					onChange={ units => onQuantityChange( units ) }
					productTitle={ title }
					disabled={ isDisabled }
				/>
			) }
		</div>
	);
};

ProductVariantCard.propTypes = {
	productData: PropTypes.object.isRequired,
	variants: PropTypes.array.isRequired,
	activeVariantId: PropTypes.number,
	onSelectVariant: PropTypes.func.isRequired,
	onInfoClick: PropTypes.func.isRequired,
	vatMode: PropTypes.oneOf( [ 'including', 'excluding' ] ).isRequired,
	isDisabled: PropTypes.bool,
	allowQuantity: PropTypes.bool,
	quantity: PropTypes.number,
	onQuantityChange: PropTypes.func,
};

ProductVariantCard.defaultProps = {
	activeVariantId: null,
	isDisabled: false,
	allowQuantity: false,
	quantity: 1,
	onQuantityChange: null,
};

export default ProductVariantCard;
